import { CurrencyDataType } from "@/api/CurrencyApi";
import { DocumentsEntryType } from "@components/dynamic/DocumentStatusList";
import { isValidEDocument } from "./EDocument";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import moment from "moment";
import { DebitnoteDataType } from "@/api/DebitnoteApi";
import { InvoicePaymentDataType } from "@/api/InvoicePaymentApi";
import { BuyInvoiceType, CLIENT_CREDIT_DOCUMENT_PREFIX, CURRENCIES_CODES, INVOICE_PAYMENT_ACTIVE_STATUS, INVOICE_PAYMENT_CANCELLED_STATUS, PROVIDER_CREDITNOTE_TYPE_CODE } from "./Constants";
import { ProviderDataType } from "@/api/ProviderApi";
import { ClientStatusFormData } from "@/formTypes/ClientStatusFormDataType";
import { SettlementApi } from "@/api/SettlementApi";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InterestEntryDataType } from "@/api/InterestEntryApi";
import { ProviderStatusFormData } from "@/formTypes/ProviderStatusFormDataType";
import { ProviderInvoiceApi, ProviderInvoiceDataType } from "@/api/ProviderInvoiceApi";
import { ProviderNoteDataType } from "@/api/ProviderNoteApi";
import { ProviderInvoicePaymentDataType } from "@/api/ProviderInvoicePaymentApi";
import { ReportDataProps } from "@components/dynamic/ReportPage";
import { ExpenseDataType } from "@/api/ExpenseApi";
import { toNumberFormat } from "./Utils";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { BuyInvoicePaymentApi, BuyInvoicePaymentDataType } from "@/api/BuyInvoicePaymentApi";
import { ProviderRecibeApi } from "@/api/ProviderRecibeApi";
import { ClientCreditApi, ClientCreditDataType } from "@/api/ClientCreditApi";
import { CurrencyApi } from "@/api/CurrencyApi";

export type ClientStatusRowData = {
    id: string;
    numero: string;
    sequence: string;
    nombreFacturacion: string;
    fecha: Date;
    total: number;
    saldo: number;
    diasFacturado: number;
    diasVencida: number;
    interest: number;
    plazo: number,
    interesPayiedDays: number,
    interestByDay: number,
    interestExempt?: boolean,
    recibo?: string | null;
    reciboId?: string | null;
    currency: CurrencyDataType,
    isInterestSettlement?: boolean,
    hasNote?: boolean,
    hasPayment?: boolean,
    noInterest?: boolean,
    pagada?: boolean,
    anulada?: boolean,
    exchangeRate: number,
    totalNotes: number;
    totalPagos: number;
    clientId?: string | null;
    providerId?: string | null;
    kind: "invoice" | "settlement" | "provider-invoice" | "buyInvoice" | "clientCredit"
};

export type InterestConfigType = {
    interestOnDue: number,
    isInterestOnDue: boolean,
    interestExemptedDocuments?: string[]
};

const daysBetween = (from: Date, to: Date) => {
    const fromDate = moment(from).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const toDate = moment(to).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    return toDate.diff(fromDate, "days");
}

const calculateDaysBetween = (projectData: string, docDate: Date, termsDays?: number): { diasFacturado: number; diasVencida: number } => {
    const projectDate = moment(projectData);
    const elapsed = daysBetween(docDate, projectDate.toDate());
    const overdue = Math.max(0, elapsed - (termsDays ?? 0));
    return { diasFacturado: elapsed, diasVencida: overdue };
};

export const parseDate = (raw: string): Date => {
    const iso = raw.includes("T") ? raw : raw.replace(" ", "T");
    const d = moment(iso)
    return d.isValid() ? d.toDate() : new Date();
};

const calculatePayiedInterestDays = (interestEntries: InterestEntryDataType[]) => {
    return interestEntries
        .filter(ie => !ie.anulado && ie.days)
        .map(ie => Number(ie.days || 0))
        .reduce((a, v) => {
            return a + v;
        }, Number(0))

}

const calculateDocumentInterest = (dayOverDue: number, total: number, monthlyInterest: number, interestExempt: boolean = false): number => {
    if (interestExempt) {
        return 0;
    }
    const parseDayOverDue = dayOverDue > 0 ? dayOverDue : 0;
    const dayInterest = monthlyInterest / 30 / 100; // 30 days = 1 month
    return total * dayInterest * parseDayOverDue;
}

const normalizeBuyInvoice = async (projectData: string, { data }: Extract<DocumentsEntryType, { kind: "buyInvoice" }>, interestConfig: InterestConfigType): Promise<ClientStatusRowData> => {
    const fecha = parseDate(`${data.fecha}`);
    const { diasFacturado, diasVencida } = calculateDaysBetween(projectData, fecha, Number(data.plazo || 0));
    const originalInvoiceTotal = Number(data.total || 0);
    let totalNotas = Number(0);
    let totalAbonos = Number(0);

    let invoiceTotal = originalInvoiceTotal;
    let hasNote = false;

    // const creditnoteTotal = data.creditnotes?.
    //     filter((cn: CreditnoteDataType) => isValidEDocument(`${cn.edocument_status_payload}`)).
    //     reduce((a, cn: CreditnoteDataType) => {
    //         return a + Number(cn.total)
    //     }, Number(0))

    // if (creditnoteTotal > 0) {
    //     hasNote = true
    //     invoiceTotal -= creditnoteTotal
    //     totalNotas += creditnoteTotal
    // }

    // if (data.apliedCreditnote) {
    //     const apliedCreditnoteAmount = Number(data.apliedCreditnote.total || 0);
    //     if (apliedCreditnoteAmount > 0) {
    //         hasNote = true
    //         invoiceTotal -= apliedCreditnoteAmount
    //         totalAbonos += apliedCreditnoteAmount
    //     }
    // }

    // const debitnoteTotal = data.debitnotes?.
    //     filter((cn: DebitnoteDataType) => isValidEDocument(`${cn.edocument_status_payload}`)).
    //     reduce((a, cn: DebitnoteDataType) => {
    //         return a + Number(cn.total)
    //     }, Number(0))

    // if (debitnoteTotal > 0) {
    //     hasNote = true
    //     invoiceTotal += debitnoteTotal
    //     totalNotas -= creditnoteTotal
    // }

    // if (invoiceTotal === originalInvoiceTotal) {
    //     hasNote = false
    // }

    const { items: buyInvoicePayments } = await BuyInvoicePaymentApi.fetchAllByBuyInvoice(`${data.id}`)
    const providerRecibe = await ProviderRecibeApi.get(`${data.providerRecibeId}`)

    const totalPagos = buyInvoicePayments
        .filter((cn: BuyInvoicePaymentDataType) => cn.estado == INVOICE_PAYMENT_ACTIVE_STATUS)
        .reduce((a, cn: BuyInvoicePaymentDataType) => {
            return a + Number(cn.monto)
        }, Number(0))

    if (totalPagos) {
        invoiceTotal -= totalPagos
        totalAbonos += totalPagos
    }

    let interesPayiedDays = 0
    let interest = 0
    let interestByDay = 0

    return {
        kind: "buyInvoice",
        id: `${data.id}`,
        providerId: `${data.providerId}`,
        numero: `FEC-${data.numero}`,
        sequence: `${data.sequence}`,
        nombreFacturacion: `${data.nombre_factura}`,
        fecha,
        total: Number(data.total || 0),
        saldo: Math.max(0, invoiceTotal),
        diasFacturado,
        diasVencida,
        pagada: data.status == "pagada",
        anulada: data.anulada || false,
        recibo: providerRecibe ? `${providerRecibe.numero_recibo || ""}` : "",
        reciboId: data.providerRecibeId,
        currency: data.currency,
        hasNote,
        hasPayment: totalAbonos > 0 && !data.pagada,
        exchangeRate: Number(data.exchange_rate || 1),
        totalNotes: totalNotas,
        totalPagos: totalAbonos,
        plazo: Number(data.plazo || 0),
        interesPayiedDays,
        interest,
        interestByDay
    };
};


const normalizeInvoice = (projectData: string, { data }: Extract<DocumentsEntryType, { kind: "invoice" }>, interestConfig: InterestConfigType): ClientStatusRowData => {
    const fecha = parseDate(`${data.fecha}`);
    const { diasFacturado, diasVencida } = calculateDaysBetween(projectData, fecha, Number(data.plazo || 0));
    const originalInvoiceTotal = Number(data.total || 0);
    let totalNotas = Number(0);
    let totalAbonos = Number(0);

    let invoiceTotal = originalInvoiceTotal;
    let hasNote = false;

    const creditnoteTotal = data.creditnotes?.
        filter((cn: CreditnoteDataType) => isValidEDocument(`${cn.edocument_status_payload}`)).
        reduce((a, cn: CreditnoteDataType) => {
            return a + Number(cn.total)
        }, Number(0))

    if (creditnoteTotal > 0) {
        hasNote = true
        invoiceTotal -= creditnoteTotal
        totalNotas += creditnoteTotal
    }

    if (data.apliedCreditnote) {
        const apliedCreditnoteAmount = Number(data.apliedCreditnote.total || 0);
        if (apliedCreditnoteAmount > 0) {
            hasNote = true
            invoiceTotal -= apliedCreditnoteAmount
            totalAbonos += apliedCreditnoteAmount
        }
    }

    const debitnoteTotal = data.debitnotes?.
        filter((cn: DebitnoteDataType) => isValidEDocument(`${cn.edocument_status_payload}`)).
        reduce((a, cn: DebitnoteDataType) => {
            return a + Number(cn.total)
        }, Number(0))

    if (debitnoteTotal > 0) {
        hasNote = true
        invoiceTotal += debitnoteTotal
        totalNotas -= creditnoteTotal
    }

    if (invoiceTotal === originalInvoiceTotal) {
        hasNote = false
    }

    const totalPagos = data.invoicePayments?.
        filter((cn: InvoicePaymentDataType) => cn.estado == INVOICE_PAYMENT_ACTIVE_STATUS).
        reduce((a, cn: InvoicePaymentDataType) => {
            return a + Number(cn.monto)
        }, Number(0))

    if (totalPagos) {
        invoiceTotal -= totalPagos
        totalAbonos += totalPagos
    }

    let interesPayiedDays = 0
    let interest = 0
    let interestByDay = 0

    if (interestConfig.isInterestOnDue && !data.interest_exempt) {
        const isInterestExempted = (interestConfig.interestExemptedDocuments || []).includes(`${data.id}`);
        interesPayiedDays = calculatePayiedInterestDays(data.interestEntries || [])
        interest = calculateDocumentInterest(diasVencida - interesPayiedDays, invoiceTotal, interestConfig.interestOnDue, isInterestExempted)
        interestByDay = calculateDocumentInterest(1, invoiceTotal, interestConfig.interestOnDue, isInterestExempted)
    }

    return {
        kind: "invoice",
        id: `${data.id}`,
        clientId: `${data.clientId}`,
        numero: `${data.numero}`,
        sequence: `${data.sequence}`,
        nombreFacturacion: `${data.nombre_factura}`,
        fecha,
        total: Number(data.total),
        saldo: Math.max(0, invoiceTotal),
        diasFacturado,
        diasVencida,
        pagada: data.status == "pagada",
        anulada: data.anulada || false,
        recibo: data.recibe ? data.recibe.numero : "",
        reciboId: `${data.recibeId}`,
        currency: data.currency,
        hasNote,
        hasPayment: totalAbonos > 0 && !data.pagada,
        exchangeRate: Number(data.exchange_rate || 1),
        totalNotes: totalNotas,
        totalPagos: totalAbonos,
        plazo: data.plazo || 0,
        interesPayiedDays,
        interest,
        interestByDay,
        interestExempt: data.interest_exempt || false
    };
};

const normalizeSettlement = (projectData: string, { data }: Extract<DocumentsEntryType, { kind: "settlement" }>, interestConfig: InterestConfigType): ClientStatusRowData => {
    const fecha = parseDate(`${data.fecha}`);
    const { diasFacturado, diasVencida } = calculateDaysBetween(projectData, fecha, Number(data.plazo || 0));

    let settlementTotal = Number(data.total || 0);

    const totalAbonos = data.settlementPayments?.
        filter((cn: InvoicePaymentDataType) => cn.estado == INVOICE_PAYMENT_ACTIVE_STATUS).
        reduce((a, cn: InvoicePaymentDataType) => {
            return a + Number(cn.monto)
        }, Number(0))

    if (totalAbonos) {
        settlementTotal -= totalAbonos
    }

    let interesPayiedDays = 0
    let interest = 0
    let interestByDay = 0

    if (interestConfig.isInterestOnDue && !data.interest_exempt) {
        interesPayiedDays = calculatePayiedInterestDays(data.interestEntries || [])
        interest = calculateDocumentInterest(diasVencida - interesPayiedDays, settlementTotal, interestConfig.interestOnDue)
        interestByDay = calculateDocumentInterest(1, settlementTotal, interestConfig.interestOnDue)
    }

    return {
        kind: "settlement",
        id: `${data.id}`,
        clientId: `${data.clientId}`,
        numero: `${data.numero}`,
        sequence: `Liquidacion`,
        nombreFacturacion: `${data.nombre_factura}`,
        fecha,
        total: Number(data.total),
        saldo: Math.max(0, settlementTotal),
        diasFacturado,
        diasVencida,
        recibo: data.recibe ? data.recibe.numero : "",
        reciboId: `${data.recibeId}`,
        currency: data.currency,
        hasPayment: totalAbonos > 0 && !data.pagada,
        pagada: data.status == "pagada",
        exchangeRate: Number(data.exchange_rate || 1),
        totalNotes: 0,
        totalPagos: totalAbonos,
        plazo: data.plazo || 0,
        interesPayiedDays,
        interest,
        interestByDay,
        interestExempt: data.interest_exempt || false
    };
};

const normalizeProvider = (projectData: string, { data }: Extract<DocumentsEntryType, { kind: "providerInvoice" }>, interestConfig: InterestConfigType): ClientStatusRowData => {
    const fecha = parseDate(`${data.fecha}`);
    const { plazo = 0, nombre = "" }: ProviderDataType = data.provider || {};
    const originalInvoiceTotal = Number(data.total || 0);
    let hasNote = false;

    if (data.providerNotes && data.providerNotes?.length > 0) {
        hasNote = true
    }

    let invoiceTotal = Number(data.total || 0);
    const totalNotas = data.providerNotes?.
        reduce((a, n: ProviderNoteDataType) => {
            let totalNota = Number(n.total || 0);
            if (n.tipo == `${PROVIDER_CREDITNOTE_TYPE_CODE}`) {
                totalNota = -1 * totalNota;
            }
            return a + totalNota
        }, Number(0))

    if (totalNotas) {
        invoiceTotal += totalNotas
    }

    if (invoiceTotal === originalInvoiceTotal) {
        hasNote = false
    }

    // Criterio "no cancelado" en vez de `== active`: los abonos historicos se
    // registraron antes de que el modelo tuviera `estado` y deben seguir contando.
    const totalAbonos = data.providerInvoicePayments?.
        filter((cn: ProviderInvoicePaymentDataType) => cn.estado != INVOICE_PAYMENT_CANCELLED_STATUS).
        reduce((a, cn: ProviderInvoicePaymentDataType) => {
            return a + Number(cn.monto)
        }, Number(0))

    if (totalAbonos) {
        invoiceTotal -= totalAbonos
    }

    if (data.status == "pagada") {
        invoiceTotal = 0
    }

    const { diasFacturado, diasVencida } = calculateDaysBetween(projectData, fecha, Number(plazo || 0));
    return {
        kind: "provider-invoice",
        id: `${data.id}`,
        providerId: `${data.providerId}`,
        numero: `${data.numero}`,
        sequence: `${data.sequence}`,
        nombreFacturacion: `${nombre}`,
        fecha,
        total: Number(data.total || 0),
        saldo: Math.max(0, invoiceTotal),
        diasFacturado,
        diasVencida,
        recibo: data.providerRecibe?.numero_recibo || "",
        reciboId: data.providerRecibeId,
        hasNote,
        pagada: data.status == "pagada",
        hasPayment: totalAbonos > 0 && !data.pagada,
        currency: data.currency,
        exchangeRate: Number(data.exchange_rate || 1),
        totalNotes: totalNotas,
        totalPagos: totalAbonos,
        interest: 0,
        plazo: Number(plazo || 0),
        interesPayiedDays: 0,
        interestByDay: 0
    };
};

/**
 * Saldo a favor del cliente. Es la unica fila del estado de cuenta con saldo
 * negativo: al seleccionarla junto a facturas rebaja el total a cobrar.
 * La moneda se resuelve aparte porque el modelo no declara relaciones.
 */
const normalizeClientCredit = async (projectData: string, { data }: Extract<DocumentsEntryType, { kind: "clientCredit" }>): Promise<ClientStatusRowData> => {
    const fecha = parseDate(`${data.created}`);
    const { diasFacturado } = calculateDaysBetween(projectData, fecha, 0);
    const monto = Number(data.monto || 0);
    const disponible = monto - Number(data.aplicado || 0);
    const currency = data.currencyId ? await CurrencyApi.get(`${data.currencyId}`) : {};

    return {
        kind: "clientCredit",
        id: `${data.id}`,
        clientId: `${data.clientId}`,
        numero: `${CLIENT_CREDIT_DOCUMENT_PREFIX}-${data.numero}`,
        sequence: "Saldo a Favor",
        nombreFacturacion: `${data.clientNombre || ""}`,
        fecha,
        total: -monto,
        saldo: -disponible,
        diasFacturado,
        diasVencida: 0,
        pagada: false,
        anulada: false,
        recibo: data.recibeNumero || "",
        reciboId: data.recibeId || "",
        currency,
        hasNote: false,
        hasPayment: false,
        exchangeRate: Number(data.exchangeRate || 1),
        totalNotes: 0,
        totalPagos: 0,
        plazo: 0,
        interesPayiedDays: 0,
        interest: 0,
        interestByDay: 0,
        interestExempt: false
    };
};

export const normalizeClientStatusRow = async (projectData: string, item: DocumentsEntryType, interestConfig: InterestConfigType): Promise<ClientStatusRowData> => {
    switch (item.kind) {
        case "invoice":
            return normalizeInvoice(projectData, item, interestConfig);
        case "settlement":
            return normalizeSettlement(projectData, item, interestConfig);
        case "providerInvoice":
            return normalizeProvider(projectData, item, interestConfig);
        case "buyInvoice":
            return normalizeBuyInvoice(projectData, item, interestConfig);
        case "clientCredit":
            return normalizeClientCredit(projectData, item);
        default:
            // Exhaustive check
            const _never: never = item;
            throw new Error(`Tipo no soportado: ${(item as any)?.kind}`);
    }
};


const getClientDocumentFilters = (formData: ClientStatusFormData): any => {
    const { kind, endDate, zoneId, clientId } = formData;
    let filters: any[] = []

    if (clientId != "0" && clientId != "-2" && !!clientId) {
        filters.push({ "clientId": { eq: clientId } })
    }

    if (zoneId != "-2" && !!zoneId) {
        filters.push({
            "client": {
                zoneId: { eq: zoneId }
            }
        })
    }

    if (kind == "pendientes" || kind == "vencidas") {
        filters.push({ anulada: { eq: false } })
    } else if (kind == "pagadas") {
        filters.push({ anulada: { eq: false } })
    } else if (kind == "anuladas") {
        filters.push({ anulada: { eq: true } })
    }
    if (kind == "vencidas") {
        filters.push({
            plazo: {
                expireDataLte: {
                    dateField: "fecha",
                    endDate
                }
            }
        })
    }
    filters.push({ fecha: { dateLte: { endDate } } })
    return { and: filters };
}

const getProviderDocumentFilters = (formData: ProviderStatusFormData): any => {
    const { kind, endDate, providerId, plazo } = formData;
    let filters: any[] = []

    if (providerId != "-2" && !!providerId) {
        filters.push({ "providerId": { eq: providerId } })
    }

    if (kind == "vencidas") {
        const targetExpiratedDate = moment().subtract(plazo, "days");
        const expireData = targetExpiratedDate.format("YYYY-MM-DD")

        filters.push({
            fecha: {
                dateGte: {
                    expireData
                }
            }
        })
    }
    filters.push({ fecha: { dateLte: { endDate } } })
    return { and: filters };
}

const loadInvoices = async (formData: ClientStatusFormData): Promise<DocumentsEntryType[]> => {
    const { clientId, kind, endDate } = formData;

    const filters = getClientDocumentFilters(formData);

    const hasSpecificClient = !!clientId && clientId !== "0" && clientId !== "-2";

    if (kind === "todas" && hasSpecificClient) {
        const { items: invoices } = await InvoiceApi.fetchAllByClientDate(`${clientId}`, endDate, filters);

        return invoices.map(i => {
            const entry: DocumentsEntryType = {
                data: i,
                kind: "invoice"
            };
            return entry;
        });
    }

    let statusFilter = "pendiente";
    let clientIndex = clientId == -2 ? '' : clientId || "";

    if (kind == "pagadas") {
        statusFilter = "pagada";
    } else if (kind == "anuladas") {
        statusFilter = "anulada";
    }

    const { items: invoices } = await InvoiceApi.fetchAllForStatus(`${clientIndex}`, statusFilter, filters);

    return invoices.map(i => {
        const entry: DocumentsEntryType = {
            data: i,
            kind: "invoice"
        };
        return entry;
    });
}

const loadSettlement = async (formData: ClientStatusFormData): Promise<DocumentsEntryType[]> => {
    const { clientId, kind, endDate } = formData;

    const filters = getClientDocumentFilters(formData);

    const hasSpecificClient = !!clientId && clientId !== "0" && clientId !== "-2";

    if (kind === "todas" && hasSpecificClient) {
        const { items: settlements } = await SettlementApi.fetchAllByClientDate(`${clientId}`, endDate, filters);

        return settlements.map(i => {
            const entry: DocumentsEntryType = {
                data: i,
                kind: "settlement"
            };
            return entry;
        });
    }

    let statusFilter = "pendiente";
    let clientIndex = clientId == -2 ? '' : clientId;

    if (kind == "pagadas") {
        statusFilter = "pagada";
    } else if (kind == "anuladas") {
        statusFilter = "anulada";
    }

    const { items: settlements } = await SettlementApi.fetchAllForStatus(`${clientIndex}`, statusFilter, filters);

    return settlements.map(i => {
        const entry: DocumentsEntryType = {
            data: i,
            kind: "settlement"
        };
        return entry;
    });
}


const loadProviderInvoices = async (formData: ProviderStatusFormData): Promise<DocumentsEntryType[]> => {
    const { providerId, kind } = formData

    let statusFilter = "pendiente";
    let selectedProviderId = providerId == -2 ? '' : providerId

    if (kind == "pagadas") {
        statusFilter = "pagada"
    } else if (kind == "anuladas") {
        statusFilter = "anulada"
    }

    const filters = getProviderDocumentFilters(formData)
    const { items: providerInvoices } = await ProviderInvoiceApi.fetchAllForStatus(`${selectedProviderId}`, statusFilter, filters)

    return providerInvoices.map(i => {
        const entry: DocumentsEntryType = {
            data: i,
            kind: "providerInvoice"
        }
        return entry
    })
}

const loadBuyInvoices = async (formData: ProviderStatusFormData, buyInvoiceType: BuyInvoiceType): Promise<DocumentsEntryType[]> => {
    const { providerId, kind } = formData

    let statusFilter = "pendiente";
    let selectedProviderId = providerId == -2 ? '' : providerId

    if (kind == "pagadas") {
        statusFilter = "pagada"
    } else if (kind == "anuladas") {
        statusFilter = "anulada"
    }

    const filters = getProviderDocumentFilters(formData)
    const { items: buyInvoices } = await BuyInvoiceApi.fetchAllForStatus(`${selectedProviderId}`, statusFilter, buyInvoiceType, filters)

    return buyInvoices.map(i => {
        const entry: DocumentsEntryType = {
            data: i,
            kind: "buyInvoice"
        }
        return entry
    })
}

const documentNumero = (entry: DocumentsEntryType): string => {
    return entry.kind == "clientCredit" ? "" : `${entry.data.numero ?? ""}`;
}

const documentFecha = (entry: DocumentsEntryType): string => {
    return entry.kind == "clientCredit" ? `${entry.data.created ?? ""}` : `${entry.data.fecha ?? ""}`;
}

/**
 * Saldos a favor disponibles del cliente, para mostrarlos como linea negativa
 * en el estado de cuenta. Solo aplica cuando hay un cliente concreto y el
 * filtro contempla documentos con saldo pendiente.
 */
const loadClientCredits = async (formData: ClientStatusFormData): Promise<DocumentsEntryType[]> => {
    const { clientId, kind, endDate } = formData;

    const hasSpecificClient = !!clientId && clientId !== "0" && clientId !== "-2";
    const supportedKind = kind == "pendientes" || kind == "vencidas" || kind == "todas";

    if (!hasSpecificClient || !supportedKind) {
        return [];
    }

    const { items: clientCredits } = await ClientCreditApi.fetchAvailableByClient(`${clientId}`, endDate);

    return clientCredits.map((c: ClientCreditDataType) => {
        const entry: DocumentsEntryType = {
            data: c,
            kind: "clientCredit"
        };
        return entry;
    });
}

export const loadClientStatusData = async (formData: ClientStatusFormData): Promise<DocumentsEntryType[]> => {
    let sortFunction = (a: DocumentsEntryType, b: DocumentsEntryType) => Number(documentNumero(a)) - Number(documentNumero(b))
    if (formData.clientId == "-2") {
        sortFunction = (a: DocumentsEntryType, b: DocumentsEntryType) => {
            const nameA = (a.data as InvoiceDataType).nombre_factura || "";
            const nameB = (b.data as InvoiceDataType).nombre_factura || "";

            const nameCompare = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
            if (nameCompare !== 0) {
                return nameCompare;
            }

            const numA = Number(documentNumero(a)) || 0;
            const numB = Number(documentNumero(b)) || 0;
            return numA - numB;
        };
    }

    const invoiceDocuments = await loadInvoices(formData)
    const settlementDocuments = await loadSettlement(formData)
    const clientCreditDocuments = await loadClientCredits(formData)

    const documents = [
        ...invoiceDocuments,
        ...settlementDocuments,
    ];

    // Los saldos a favor no tienen consecutivo, van siempre al final del listado.
    return [
        ...documents.sort(sortFunction),
        ...clientCreditDocuments
    ]
}

export const loadProviderStatusData = async (formData: ProviderStatusFormData): Promise<DocumentsEntryType[]> => {
    const providerInvoiceDocuments = await loadProviderInvoices(formData)
    const buyInvoiceDocuments = await loadBuyInvoices(formData, "Buy")

    let sortFunction = (a: DocumentsEntryType, b: DocumentsEntryType) => {
        const dataA = moment(documentFecha(a))
        const dataB = moment(documentFecha(b))
        return dataA.unix() - dataB.unix()
    }

    if (formData.providerId == "-2") {
        sortFunction = (a: DocumentsEntryType, b: DocumentsEntryType) => {
            const nameA = (a.data as ProviderInvoiceDataType).provider?.nombre || "";
            const nameB = (b.data as ProviderInvoiceDataType).provider?.nombre || "";

            const nameCompare = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
            if (nameCompare !== 0) {
                return nameCompare;
            }

            const dataA = moment(documentFecha(a))
            const dataB = moment(documentFecha(b))
            return dataA.unix() - dataB.unix()
        };
    }

    return [
        ...providerInvoiceDocuments,
        ...buyInvoiceDocuments
    ].sort(sortFunction);
}

export type DocumentSummaryData = {
    subtotal: number,
    descuento: number,
    impuestos: number,
    total: number,
    totalCreditNotes?: number
    totalDebitNotes?: number
}

export const calculateProviderInvoiceSummaryData = (invoices: ProviderInvoiceDataType): DocumentSummaryData => {
    let { subtotal = 0, descuento = 0, impuestos = 0, total = 0 } = invoices;

    return invoices.providerNotes?.reduce((a, pn: ProviderNoteDataType) => {
        if (pn.tipo == `${PROVIDER_CREDITNOTE_TYPE_CODE}`) {
            a.subtotal -= Number(pn.subtotal || 0)
            a.descuento -= Number(pn.descuento || 0)
            a.impuestos -= Number(pn.impuestos || 0)
            a.total -= Number(pn.total || 0)
        } else {
            a.subtotal += Number(pn.subtotal || 0)
            a.descuento += Number(pn.descuento || 0)
            a.impuestos += Number(pn.impuestos || 0)
            a.total += Number(pn.total || 0)
        }
        return a
    }, {
        subtotal,
        descuento,
        impuestos,
        total
    })
}
export const calculateBuyInvoiceSummaryData = (invoice: BuyInvoiceDataType): DocumentSummaryData => {
    let { subtotal = 0, descuento = 0, impuestos = 0, total = 0 } = invoice;
    return {
        subtotal,
        descuento,
        impuestos,
        total
    }

    // return invoice.providerNotes?.reduce((a, pn: ProviderNoteDataType) => {
    //     if (pn.tipo == `${PROVIDER_CREDITNOTE_TYPE_CODE}`) {
    //         a.subtotal -= Number(pn.subtotal || 0)
    //         a.descuento -= Number(pn.descuento || 0)
    //         a.impuestos -= Number(pn.impuestos || 0)
    //         a.total -= Number(pn.total || 0)
    //     } else {
    //         a.subtotal += Number(pn.subtotal || 0)
    //         a.descuento += Number(pn.descuento || 0)
    //         a.impuestos += Number(pn.impuestos || 0)
    //         a.total += Number(pn.total || 0)
    //     }
    //     return a
    // }, {
    //     subtotal,
    //     descuento,
    //     impuestos,
    //     total
    // })
}

export const generateInvoiceNoteData = (invoice: InvoiceDataType, notes: CreditnoteDataType[] | DebitnoteDataType[], prefix: string = "", excludeDocumentFromTotal: boolean = false): ReportDataProps[] => {
    return notes
        .filter(note => isValidEDocument(`${note.edocument_status_payload}`))
        .map(note => {
            const date = moment(note.created, "YYYY-MM-DD")
            const formatDate = date.format("DD-MM-YYYY")
            let amountPrefix = '';
            const currencyId = Number(note.currency?.currency_id || 1);

            if (prefix == 'NC') {
                amountPrefix = '-';
            }

            const data: ReportDataProps = {
                excludeFromTotal: excludeDocumentFromTotal,
                values: [
                    `${prefix}-${note.numero}`,
                    formatDate,
                    invoice.client?.nombre || invoice.nombre_factura,
                    '',
                    '',
                    `${amountPrefix}${toNumberFormat(`${note.subtotal}`)}`,
                    `${amountPrefix}${toNumberFormat(`${note.descuento}`)}`,
                    `${amountPrefix}${toNumberFormat(`${note.impuestos}`)}`,
                    `${amountPrefix}${toNumberFormat(`${note.total}`)}`,
                    CURRENCIES_CODES[currencyId],
                    `${note.exchange_rate}`,
                    '',
                    ''
                ]
            }
            return data
        })
}

export const calculateInvoiceSummaryData = (invoices: InvoiceDataType, startData: string, endData: string): DocumentSummaryData => {
    let { subtotal = 0, descuento = 0, impuestos = 0, total = 0 } = invoices;

    let summary: DocumentSummaryData = invoices.creditnotes?.reduce((a: DocumentSummaryData, cn: CreditnoteDataType) => {
        if (`${cn.created}` <= endData && isValidEDocument(`${cn.edocument_status_payload}`)) {
            a.subtotal -= Number(cn.subtotal || 0)
            a.descuento -= Number(cn.descuento || 0)
            a.impuestos -= Number(cn.impuestos || 0)
            a.total -= Number(cn.total || 0)
            a.totalCreditNotes = Number(a.totalCreditNotes || 0) + Number(cn.total || 0)
        }
        return a
    }, {
        subtotal,
        descuento,
        impuestos,
        total,
        totalCreditNotes: 0,
        totalDebitNotes: 0
    })

    summary = invoices.debitnotes?.reduce((a: DocumentSummaryData, dn: DebitnoteDataType) => {
        if (`${dn.created}` <= endData && isValidEDocument(`${dn.edocument_status_payload}`)) {
            a.subtotal += Number(dn.subtotal || 0)
            a.descuento += Number(dn.descuento || 0)
            a.impuestos += Number(dn.impuestos || 0)
            a.total += Number(dn.total || 0)
            a.totalDebitNotes = Number(a.totalDebitNotes || 0) + Number(dn.total || 0)
        }
        return a
    }, summary)

    return summary
}

export const calculateExpenseSummaryData = (expense: ExpenseDataType): DocumentSummaryData => {
    let { subtotal = 0, descuento = 0, impuestos = 0, monto = 0 } = expense;

    return expense.expenseNotes?.reduce((a, pn: ProviderNoteDataType) => {
        if (pn.tipo == `${PROVIDER_CREDITNOTE_TYPE_CODE}`) {
            a.subtotal -= Number(pn.subtotal || 0)
            a.descuento -= Number(pn.descuento || 0)
            a.impuestos -= Number(pn.impuestos || 0)
            a.total -= Number(pn.total || 0)
        } else {
            a.subtotal += Number(pn.subtotal || 0)
            a.descuento += Number(pn.descuento || 0)
            a.impuestos += Number(pn.impuestos || 0)
            a.total += Number(pn.total || 0)
        }
        return a
    }, {
        subtotal,
        descuento,
        impuestos,
        total: monto
    })
}


export type ReportTotalConfig = {
    dataColumnIndex: number,
    title: string,
    separatorLine?: boolean
}

export type ReportTotalDetail = {
    title: string,
    total?: number,
    currencyCode?: string,
    separatorLine?: boolean
}

const parseCurrency = (value: string): number => {
    return Number(
        value
            .replace(/[^\d,.-]/g, "")   // elimina todo lo que no sea dígito, coma, punto o signo
            .replace(/\s+/g, "")        // elimina espacios en blanco
            .replace(",", ".")          // cambia coma por punto
    );
}

export const generateCurrencyReportTotal = (reportCurrencyData: ReportDataProps[], reportTotalConfig: ReportTotalConfig[], currencyCode: string): ReportTotalDetail[] => {
    return reportTotalConfig.map(({ dataColumnIndex, title, separatorLine }) => {
        const total = reportCurrencyData.filter(r => !r.excludeFromTotal).reduce((acc, row) => {
            const value = row.values?.[dataColumnIndex];
            const formatedAmount = parseCurrency(value)
            return acc + Number(formatedAmount);
        }, 0);

        return { title, total, separatorLine, currencyCode };
    });
}

export const generateReportTotal = (reportData: ReportDataProps[], reportTotalConfig: ReportTotalConfig[], currenciesOnReport: Set<string>, currencyCodeColumn: number): ReportTotalDetail[] => {
    const currencies = [...currenciesOnReport]
    if (currencies.length == 1) {

        const valueColumns = generateCurrencyReportTotal(reportData, reportTotalConfig, currencies[0])
        return [
            { title: `Los siguiente totales estan expresados en ${currencies[0]}` },
            ...valueColumns
        ]
    } else {
        return currencies.flatMap(c => {
            const entries = Array<ReportTotalDetail>()
            entries.push({ title: `Los siguiente totales estan expresados en ${c}` })
            const currencyEntries = reportData.filter(d => d.values?.[currencyCodeColumn] == c)
            const valueColumns = generateCurrencyReportTotal(currencyEntries, reportTotalConfig, c)
            entries.push(...valueColumns)
            entries.push({ title: "separator", separatorLine: true })
            return entries
        })
    }
}